import { useState } from "react";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Scale, Timer, Clock, Copy, Check, Ban } from "lucide-react";

const money = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export const TradePlan = ({ plan, symbol, currency = "" }) => {
  const [copied, setCopied] = useState(false);
  if (!plan) return null;
  const short = plan.side === "SELL";
  const EntryIcon = short ? TrendingDown : TrendingUp;
  const ExitIcon = short ? TrendingUp : TrendingDown;
  const entryColor = short ? "text-red-400" : "text-emerald-400";
  const exitColor = short ? "text-emerald-400" : "text-red-400";
  const cur = currency ? `${currency} ` : "$";

  const asText = () =>
    [
      `--- TRADE SUGGESTION · ${symbol || plan.detected_timeframe} · ${plan.detected_timeframe} chart ---`,
      `${plan.side}`, `  • Price: ${cur}${money(plan.entry.price)}`, `  • When: ${plan.entry.condition}`, `  • Timeframe: ${plan.entry.timing}`,
      `${plan.exit.label}`, `  • Target: ${cur}${money(plan.exit.target_price)}`, `  • Stop-loss: ${cur}${money(plan.exit.stop_price)}`, `  • When: ${plan.exit.condition}`,
      `Risk/Reward: ${plan.risk_reward.ratio}`, `  • Risk: ${cur}${money(plan.risk_reward.risk)}`, `  • Reward: ${cur}${money(plan.risk_reward.reward)}`,
      `Estimated Hold: ${plan.hold_duration} (${plan.hold_candles})`, `Invalidation: ${plan.invalidation}`,
      `--- END OF SUGGESTION ---`,
    ].join("\n");

  const copy = async () => {
    try { await navigator.clipboard.writeText(asText()); setCopied(true); toast.success("Trade plan copied"); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Clipboard unavailable"); }
  };

  return (
    <div className="glass rounded-2xl p-5 relative overflow-hidden" data-testid="trade-plan-card">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-500/60 to-transparent" />
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400">Trade Suggestion</p>
          <p className="text-sm text-slate-300">Detected timeframe <span className="font-mono-num font-semibold text-white" data-testid="trade-plan-timeframe">{plan.detected_timeframe}</span></p>
        </div>
        <button onClick={copy} data-testid="trade-plan-copy-button" className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-emerald-400 transition-colors">
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy plan"}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <Step icon={EntryIcon} color={entryColor} title={plan.side} price={`${cur}${money(plan.entry.price)}`} testId="trade-plan-entry"
          rows={[["When", plan.entry.condition], ["Timeframe", plan.entry.timing]]} />
        <Step icon={ExitIcon} color={exitColor} title={plan.exit.label} price={`${cur}${money(plan.exit.target_price)}`} testId="trade-plan-exit"
          rows={[["Stop-loss", `${cur}${money(plan.exit.stop_price)}`], ["When", plan.exit.condition]]} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Metric icon={Scale} label="Risk / Reward" value={plan.risk_reward.ratio} testId="trade-plan-rr"
          sub={<>Risk <span className="text-red-400 font-mono-num">{cur}{money(plan.risk_reward.risk)}</span> · Reward <span className="text-emerald-400 font-mono-num">{cur}{money(plan.risk_reward.reward)}</span></>} />
        <Metric icon={Timer} label="Est. Hold Duration" value={plan.hold_duration} testId="trade-plan-hold" sub={<><Clock className="w-3 h-3 inline mr-1" />{plan.hold_candles} on {plan.detected_timeframe}</>} />
        <Metric icon={Ban} label="Invalidation" value={null} testId="trade-plan-invalidation" sub={plan.invalidation} className="col-span-2 sm:col-span-1" />
      </div>
    </div>
  );
};

function Step({ icon: Icon, color, title, price, rows, testId }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className={`flex items-center gap-1.5 font-display font-bold ${color}`}><Icon className="w-4 h-4" /> {title}</span>
        <span className="font-mono-num text-lg font-bold" data-testid={`${testId}-price`}>{price}</span>
      </div>
      <ul className="space-y-1.5">
        {rows.map(([k, v]) => (
          <li key={k} className="text-sm flex gap-2"><span className="text-slate-500 shrink-0 w-20">{k}</span><span className="text-slate-200 leading-snug">{v}</span></li>
        ))}
      </ul>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, testId, className = "" }) {
  return (
    <div className={`rounded-xl bg-white/[0.03] border border-white/10 p-3 ${className}`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1"><Icon className="w-3 h-3" /> {label}</p>
      {value && <p className="font-mono-num text-xl font-bold text-white leading-tight">{value}</p>}
      <p className="text-xs text-slate-400 mt-0.5 leading-snug">{sub}</p>
    </div>
  );
}
